import { useEffect, useState } from 'react';

import Dropdown from '@components/drop-down/Dropdown';
import Input from '@components/input/Input';
import { CustomFilterPanelProps } from '@components/multi-field-filter/types';
import {
  clampDaysStuckInput,
  DAYS_STUCK_OP_OPTIONS,
  DaysStuckFilterValue,
  DaysStuckOp,
  decodeDaysStuckFilter,
  encodeDaysStuckFilter,
  getDaysStuckSummary,
  isDaysStuckFilterComplete,
  isDaysStuckRangeInvalid,
  MAX_DAYS_STUCK
} from '@features/lead-management/utils/daysStuckFilter';

const DaysStuckFilterPanel = ({ value, onChange }: CustomFilterPanelProps) => {
  const decoded = decodeDaysStuckFilter(value);
  const [op, setOp] = useState<DaysStuckOp | null>(decoded?.op ?? null);
  const [days, setDays] = useState(
    decoded?.value != null ? String(decoded.value) : ''
  );
  const [daysTo, setDaysTo] = useState(
    decoded?.valueTo != null ? String(decoded.valueTo) : ''
  );

  useEffect(() => {
    const next = decodeDaysStuckFilter(value);
    setOp(next?.op ?? null);
    setDays(next?.value != null ? clampDaysStuckInput(String(next.value)) : '');
    setDaysTo(
      next?.valueTo != null ? clampDaysStuckInput(String(next.valueTo)) : ''
    );
  }, [value]);

  const emitChange = (
    nextOp: DaysStuckOp | null,
    nextDays: string,
    nextDaysTo: string
  ) => {
    if (!nextOp) {
      onChange([]);
      return;
    }

    const parsed: DaysStuckFilterValue = { op: nextOp };
    if (nextDays !== '') {
      const num = Number(nextDays);
      if (!Number.isNaN(num)) parsed.value = num;
    }
    if (nextOp === DaysStuckOp.BETWEEN && nextDaysTo !== '') {
      const num = Number(nextDaysTo);
      if (!Number.isNaN(num)) parsed.valueTo = num;
    }
    onChange(encodeDaysStuckFilter(parsed));
  };

  const draft: DaysStuckFilterValue | null = op
    ? {
        op,
        ...(days !== '' && !Number.isNaN(Number(days))
          ? { value: Number(days) }
          : {}),
        ...(op === DaysStuckOp.BETWEEN &&
        daysTo !== '' &&
        !Number.isNaN(Number(daysTo))
          ? { valueTo: Number(daysTo) }
          : {})
      }
    : null;
  const summary = getDaysStuckSummary(draft);
  const showInputs = !!op;
  const isBetween = op === DaysStuckOp.BETWEEN;
  const isRangeInvalid = isDaysStuckRangeInvalid(draft);

  const handleSelectOp = (nextOpValue: string) => {
    const nextOp = nextOpValue as DaysStuckOp;
    setOp(nextOp);
    if (nextOp !== DaysStuckOp.BETWEEN) {
      setDaysTo('');
      emitChange(nextOp, days, '');
    } else {
      emitChange(nextOp, days, daysTo);
    }
  };

  const handleClearOp = () => {
    setOp(null);
    setDays('');
    setDaysTo('');
    onChange([]);
  };

  const handleDaysChange = (raw: string) => {
    const next = clampDaysStuckInput(raw);
    setDays(next);
    emitChange(op, next, isBetween ? daysTo : '');
  };

  const handleDaysToChange = (raw: string) => {
    const next = clampDaysStuckInput(raw);
    setDaysTo(next);
    emitChange(op, days, next);
  };

  return (
    <div className="flex flex-1 flex-col gap-3 px-4 py-3">
      <div className="flex flex-col gap-1.5">
        <span className="text-sm text-cofeeNeutral7">Condition</span>
        <Dropdown
          dropdownId="days-stuck-condition"
          listId="days-stuck-condition"
          variant="v3"
          stopEventPropagation
          currentFilter={op}
          filterOptions={DAYS_STUCK_OP_OPTIONS}
          onSelectFilter={handleSelectOp}
          onClear={handleClearOp}
          placeholder="Select a condition"
          className="!h-11 w-full !min-w-0 !rounded-lg !border-cofeeNeutral4 !bg-white"
          optionsStyle="!left-0 !right-0 !w-full !min-w-full"
          labelStyle="!text-sm"
          placeholderStyle="!text-sm !text-cofeeNeutral7"
        />
      </div>

      {showInputs && !isBetween && (
        <Input
          name="days-stuck-value"
          type="number"
          value={days}
          handleInputChange={handleDaysChange}
          max={MAX_DAYS_STUCK}
          placeholder="0"
          suffixText="Days"
          inputStyle="!h-11 !pr-14"
          className="w-2/5 min-w-0 max-w-[40%]"
        />
      )}

      {showInputs && isBetween && (
        <div className="flex items-center gap-2">
          <Input
            name="days-stuck-from"
            type="number"
            value={days}
            handleInputChange={handleDaysChange}
            max={MAX_DAYS_STUCK}
            placeholder="0"
            suffixText="Days"
            inputStyle="!h-11 !pr-14"
            className="w-full"
          />
          <span className="shrink-0 text-sm text-cofeeNeutral10">and</span>
          <Input
            name="days-stuck-to"
            type="number"
            value={daysTo}
            handleInputChange={handleDaysToChange}
            max={MAX_DAYS_STUCK}
            placeholder="0"
            suffixText="Days"
            inputStyle="!h-11 !pr-14"
            className="w-full"
          />
        </div>
      )}

      {isRangeInvalid && (
        <div className="rounded-lg bg-cofeeRed1 px-3 py-2.5 text-sm font-medium text-cofeeRed6">
          The second value must be greater than the first value
        </div>
      )}

      {summary && isDaysStuckFilterComplete(draft) && (
        <div className="rounded-lg bg-cofeeGreen11 px-3 py-2.5 text-sm font-medium text-primary">
          {summary}
        </div>
      )}
    </div>
  );
};

export default DaysStuckFilterPanel;
